import axios from "axios";
import { config } from "../config";
import { Providers, ProviderState } from "@microsoft/mgt-element";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { msalConfig } from "../config";

export const submitStates = {
  idle: "Idle",
  saving: "Saving",
  saveSuccessful: "Save Successful",
  saveFailed: "Save Failed",
};

const initialState = {
  user: {},
  workshops: { upcoming: [], expired: [] },
  categories: [],
  sites: [],
  selectedWorkshop: null,
  editingWorkshop: false,
  filter: {
    search: "", //search term
    showCreatedByUser: false,
    categoryId: "", //category to filter by
    sortBy: "startDatetime",
    sortDirection: "asc",
    expired: false, //show upcoming if false, show expired if true
    createdByMe: false, //filter by workshops created by current user
  },
  status: {
    workshopLoading: false,
    workshopSaving: false,
    listLoading: true,
    notifications: [
      {
        message: "",
        details: "",
        type: "success",
        timestamp: null,
      },
    ],
    formSubmitState: submitStates.idle,
  },
};

export const workshopSlice = createSlice({
  name: "workshops",
  initialState: initialState,
  reducers: {
    setUser: (state, { payload }) => {
      console.log(`[reducer] setUser(${payload}) called`);
      state.user = payload;
    },
    setListLoading: (state, { payload }) => {
      console.log(`[reducer] setListLoading(${payload}) called`);
      state.status.listLoading = payload;
    },
    setWorkshopLoading: (state, { payload }) => {
      console.log(`[reducer] setWorkshopLoading(${payload}) called`);
      state.status.workshopLoading = payload;
    },
    setWorkshopSaving: (state, { payload }) => {
      console.log(`[reducer] setWorkshopSaving(${payload}) called`);
      state.status.workshopSaving = payload;
    },
    setFormSubmitState: (state, { payload }) => {
      console.log(`reducer setFormSubmitState(${payload}) called`);
      state.status.formSubmitState = payload;
    },
    setEditingWorkshop: (state, { payload }) => {
      console.log(`[reducer]setEditingWorkshop(${payload}) called`);
      state.editingWorkshop = payload;
    },
    addNotification: (state, { payload }) => {
      //adds a new notification onto the array of notifications
      payload.type = payload.type || "success";
      state.status.notifications.push({
        message: payload.message,
        type: payload.type,
        details: payload.details,
        timestamp: Date.now(),
      });
    },
    setCategories(state, { payload }) {
      console.log(`[reducer] setCategories() called`);
      payload.sort(compareValues("description"));
      //sort alphabetically and add to state
      state.categories = payload.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name < b.name) return 1;
        return 0;
      });
    },
    setSites(state, { payload }) {
      console.log(`[reducer] setSites() called`);
      //sort by site name
      payload.sort(compareValues("siteName"));
      state.sites = payload;
    },
    setWorkshops(state, { payload }) {
      console.log(`[reducer] setWorkshops() called`);
      //expired: if the workshops are from before today
      //replace: if true replace state workshops, if false add to state workshops
      //workshops: the workshops
      const { expired, replace, workshops } = payload;

      if (expired)
        state.workshops.expired = replace
          ? workshops
          : [...state.workshops.expired, ...workshops];
      else
        state.workshops.upcoming = replace
          ? workshops
          : [...state.workshops.upcoming, ...workshops];
    },
    setSelectedWorkshop(state, { payload }) {
      console.log(`[reducer] setSelectedWorkshop() called`);
      state.selectedWorkshop = payload;
    },
    setFilterSearchString(state, { payload }) {
      console.log(`[reducer] setFilterSearch(${payload}) called`);
      state.filter.search = payload;
    },
    setFilterCategoryId(state, { payload }) {
      console.log(`[reducer] setFilterCategoryId(${payload}) called`);
      state.filter.categoryId = payload;
    },
    setSort(state, { payload }) {
      console.log(`[reducer] setSort() called`);
      state.filter.sortBy = payload?.sortBy ?? state.filter.sortBy;
      state.filter.sortDirection =
        payload?.sortDirection ?? state.filter.sortDirection;
    },
    setFilter(state, { payload }) {
      //update the filter variables with the changed value from payload
      console.log(`[reducer] setFilter() called`);
      state.filter = Object.assign(state.filter, payload);
    },
    resetFilter(state, { payload }) {
      console.log("[reducer] resetFilter() called");
      state.filter = {
        ...state.filter,
        showCreatedByUser: false,
        search: "",
        categoryId: "",
        sortBy: "startDatetime",
        sortDirection: "asc",
      };
    },
    updateWorkshop(state, { payload }) {
      console.log(`[reducer] updateWorkshop() called`);
      //replace workshop with new data
      state.workshops.upcoming = state.workshops.upcoming.map((w) => {
        if (w.workshopId === payload.workshopId) {
          return payload;
        }
        return w;
      });
    },
  },
});

export const {
  setUser,
  setListLoading,
  setWorkshopLoading,
  setWorkshopSaving,
  setFormSubmitState,
  setEditingWorkshop,
  addNotification,
  setCategories,
  setSites,
  setWorkshops,
  setSelectedWorkshop,
  setFilterSearchString,
  setFilterCategoryId,
  setSort,
  setFilter,
  resetFilter,
  updateWorkshop,
} = workshopSlice.actions;

//custom selectors=================================

//get workshops filtered by search term and selected category
const workshops = (state) => state.workshops.workshops;
const filter = (state) => state.workshops.filter;
const state = (state) => state.workshops;
export const selectFilteredWorkshops = createSelector([state], (state) => {
  const { workshops, filter } = state;

  const doFilter = (workshops) => {
    let filteredWorkshops = workshops.filter(
      (w) =>
        w.title.toLowerCase().includes(filter.search.toLowerCase()) ||
        w.tags?.toLowerCase().includes(filter.search.toLowerCase()) ||
        w.focusArea?.toLowerCase().includes(filter.search.toLowerCase())
    );
    if (filter.categoryId)
      filteredWorkshops = filteredWorkshops.filter(
        (w) => w.categoryId === filter.categoryId
      );

    if (filter.showCreatedByUser) {
      filteredWorkshops = filteredWorkshops.filter(
        (w) => w.creator.mail === state.user.username
      );
    }

    //sort workshops
    filteredWorkshops.sort(compareValues(filter.sortBy, filter.sortDirection));
    return filteredWorkshops;
  };

  let filteredUpcoming = doFilter(workshops.upcoming);
  let filteredExpired = doFilter(workshops.expired);

  return {
    upcomingWorkshops: filteredUpcoming,
    expiredWorkshops: filteredExpired,
  };
});

//async reducers==================================
export const loadCategories = () => {
  return async (dispatch) => {
    console.log(`[async reducer] loadCategories() called`);
    axios
      .get(`${config.apiEndpoint}/categories`)
      .then((response) => dispatch(setCategories(response.data.data)));
  };
};

export const loadSites = () => {
  return async (dispatch) => {
    console.log(`[async reducer] loadSites() called`);
    axios
      .get(`${config.apiEndpoint}/sites`)
      .then((response) => dispatch(setSites(response.data.data)));
  };
};

export const loadExpiredWorkshops = (getMore = false) => {
  return async (dispatch, getState) => {
    console.log(`[async reducer] loadexpiredWorkshops(${getMore}) called`);

    dispatch(setListLoading(true));

    //get last workshop date
    let expiredWorkshops = [...getState().workshops.workshops.expired];
    expiredWorkshops.sort((a, b) => {
      if (a.startDatetime < b.startDatetime) return 1;
      if (a.startDatetime > b.startDatetime) return -1;
      return 0;
    });
    let lastDate = "";
    if (expiredWorkshops.length > 0)
      lastDate = expiredWorkshops[expiredWorkshops.length - 1].startDatetime;

    //find out if the current user is an admin
    //if current user is not admin call the presenter endpoint
    const endpoint = getState().workshops.user.idTokenClaims.roles.some(
      (role) => role === "Workshops.Admin"
    )
      ? `${config.apiEndpoint}?expired=true&lastPageEnd=${lastDate}`
      : `${config.apiEndpoint}?expired=true&asPresenter=true&lastPageEnd=${lastDate}`;

    //call api to get workshops
    const token = await getAccessToken();
    axios
      .get(endpoint, getBearerHeader(token))
      .then((response) => {
        dispatch(
          setWorkshops({
            expired: true,
            replace: !getMore,
            workshops: response.data.data,
          })
        );
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Unable to load the workshop list",
          error
        );
      })
      .finally(() => dispatch(setListLoading(false)));
  };
};
//get the workshop list
export const loadWorkshops = () => {
  return async (dispatch, getState) => {
    console.log(`[async reducer] loadWorkshops() called`);

    dispatch(setListLoading(true));
    //find out if the current user is an admin
    //if current user is not admin call the presenter endpoint
    const endpoint = getState().workshops.user.idTokenClaims.roles.some(
      (role) => role === "Workshops.Admin"
    )
      ? `${config.apiEndpoint}?upcomingAndUnfinished=true`
      : `${config.apiEndpoint}?asPresenter=true`;

    //call api to get workshops
    const token = await getAccessToken();
    axios
      .get(endpoint, getBearerHeader(token))
      .then((response) => {
        dispatch(
          setWorkshops({
            expired: false,
            replace: true,
            workshops: response.data.data,
          })
        );
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Unable to load the workshop list",
          error
        );
      })
      .finally(() => dispatch(setListLoading(false)));
  };
};

export const loadWorkshop = (id) => {
  return async (dispatch) => {
    console.log(`[async reducer] loadWorkshop(${id}) called`);

    dispatch(setWorkshopLoading(true));
    const token = await getAccessToken();
    axios
      .get(`${config.apiEndpoint}/${id}/full`, getBearerHeader(token))
      .then((response) => dispatch(setSelectedWorkshop(response.data.data)))
      .catch((error) => {
        errorToNotification(dispatch, "Unable to load the workshop", error);
      })
      .finally(() => dispatch(setWorkshopLoading(false)));
  };
};

export const registerForWorkshop = (
  workshopId,
  mail,
  sendMail,
  addEvent,
  fromWaitlist = false
) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] registerForWorkshop(${workshopId},${mail}) called`
    );

    const endpoint = fromWaitlist
      ? `${config.apiEndpoint}/${workshopId}/acceptseat?mail=${mail}&sendMail=${sendMail}&addEvent=${addEvent}`
      : `${config.apiEndpoint}/${workshopId}/registration?mail=${mail}&sendMail=${sendMail}&addEvent=${addEvent}`;

    dispatch(setWorkshopSaving(true));
    const token = await getAccessToken();
    axios
      .post(endpoint, {}, getBearerHeader(token))
      .then((response) => {
        dispatch(updateWorkshop(response.data.data));
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));

        dispatch(
          addNotification({
            message: `Registration complete`,
          })
        );
        dispatch(setWorkshopSaving(false));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong registering",
          error
        );
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));

        dispatch(setWorkshopSaving(false));
      });
  };
};

export const registerGroupForWorkshop = (
  workshopId,
  mails,
  sendMail,
  addEvent
) => {
  return async (dispatch, getState) => {
    //remove blank spaces and convert mails into array at commas
    mails = mails.replace(/\s/g, "").split(",");

    console.log(`[async reducer] registerGroup(${workshopId},${mails}) called`);
    dispatch(setWorkshopSaving(true));
    const token = await getAccessToken();
    axios
      .post(
        `${config.apiEndpoint}/${workshopId}/registration/group?sendMail=${sendMail}&addEvent=${addEvent}`,
        { mails },
        getBearerHeader(token)
      )
      .then((response) => {
        //dispatch(updateWorkshop(response.data.data));
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));

        dispatch(
          addNotification({
            message: `Registration complete`,
          })
        );
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while registering",
          error
        );
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));
      })
      .finally(() => {
        dispatch(setWorkshopSaving(false));
      });
  };
};

export const unRegisterFromWorkshop = (
  workshopId,
  userMail,
  sendMail = true
) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] unRegisterFromWorkshop(${workshopId},${userMail}) called`
    );
    dispatch(setWorkshopSaving(true));
    const token = await getAccessToken();
    axios
      .delete(
        `${config.apiEndpoint}/${workshopId}/registration?mail=${userMail}&sendMail=${sendMail}`,
        getBearerHeader(token)
      )
      .then((response) => {
        dispatch(updateWorkshop(response.data.data));
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));
        dispatch(
          addNotification({
            message: `${userMail} is no longer registered for this workshop`,
          })
        );
        dispatch(setWorkshopSaving(false));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong removing user from workshop",
          error
        );
        dispatch(setWorkshopSaving(false));
      });
  };
};

//todo: update this to use an array of mails
export const joinWorkshopWaitlist = (workshopId, userMail, sendMail = true) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] joinWorkshopWaitlist(${workshopId},${userMail}) called`
    );
    dispatch(setWorkshopLoading(true));
    const token = await getAccessToken();
    axios
      .post(
        `${config.apiEndpoint}/${workshopId}/waitlist?mail=${userMail}&sendMail=${sendMail}`,
        {},
        getBearerHeader(token)
      )
      .then((response) => {
        dispatch(
          addNotification({
            message: `${userMail} is now on the waitlist for this workshop`,
          })
        );
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));
        dispatch(setWorkshopLoading(false));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong adding user to waitlist",
          error
        );
        dispatch(setWorkshopLoading(false));
      });
  };
};

export const leaveWorkshopWaitlist = (workshopId, userMail, sendMail) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] leaveWorkshopWaitlist(${workshopId},${userMail}) called`
    );
    dispatch(setWorkshopLoading(true));
    const token = await getAccessToken();
    axios
      .delete(
        `${config.apiEndpoint}/${workshopId}/waitlist?mail=${userMail}&sendMail=${sendMail}`,
        getBearerHeader(token)
      )
      .then((response) => {
        dispatch(
          addNotification({
            message: `${userMail} is no longer on the waitlist for this workshop`,
          })
        );
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));
        dispatch(setWorkshopLoading(false));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Error removing user from waitlist",
          error
        );
        dispatch(setWorkshopLoading(false));
      });
  };
};

//put (update) the workshop data to the api
export const saveWorkshop = (workshopData) => {
  return async (dispatch) => {
    console.log("[async reducer] saveWorkshop() called", workshopData);
    dispatch(setWorkshopSaving(true));
    dispatch(setFormSubmitState(submitStates.saving));
    const token = await getAccessToken();
    axios
      .put(
        `${config.apiEndpoint}/${workshopData.workshopId}`,
        workshopData,
        getBearerHeader(token)
      )
      .then((response) => {
        //update selected workshop with saved data
        dispatch(setSelectedWorkshop(response.data.data));
        //update the workshop on the list
        dispatch(updateWorkshop(response.data.data));
        dispatch(
          addNotification({
            message: `${workshopData.title} has been saved`,
          })
        );
        dispatch(setWorkshopSaving(false));
        dispatch(setFormSubmitState(submitStates.saveSuccessful));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while saving the workshop",
          error
        );
        dispatch(setWorkshopSaving(false));
        dispatch(setFormSubmitState(submitStates.saveFailed));
      });
  };
};

//post a new workshop to the api
export const saveNewWorkshop = (workshopData) => {
  return async (dispatch) => {
    console.log("[async reducer] saveNewWorkshop() called", workshopData);
    dispatch(setWorkshopSaving(true));
    dispatch(setFormSubmitState(submitStates.saving));
    const token = await getAccessToken();
    axios
      .post(`${config.apiEndpoint}`, workshopData, getBearerHeader(token))
      .then((response) => {
        //make newly saved workshop the selected one
        dispatch(setSelectedWorkshop(response.data.data));
        //reload workshop list
        dispatch(loadWorkshops());
        dispatch(setWorkshopSaving(false));
        dispatch(setFormSubmitState(submitStates.saveSuccessful));
        dispatch(
          addNotification({
            message: `${workshopData.title} has been saved`,
          })
        );
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while saving",
          error
        );
        dispatch(setWorkshopSaving(false));
        dispatch(setFormSubmitState(submitStates.saveFailed));
      });
  };
};

export const deleteWorkshop = (workshopId, navigate) => {
  return async (dispatch) => {
    console.log(`[async reducer] deleteWorkshop(${workshopId}) called`);
    dispatch(setWorkshopSaving(true));
    const token = await getAccessToken();
    axios
      .delete(`${config.apiEndpoint}/${workshopId}`, getBearerHeader(token))
      .then((response) => {
        //refresh workshop list
        dispatch(loadWorkshops());
        dispatch(setSelectedWorkshop(null));
        dispatch(setWorkshopSaving(false));
        dispatch(
          addNotification({
            message: "The workshop was deleted",
          })
        );
        navigate("/");
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while deleting",
          error
        );
        dispatch(setWorkshopSaving(false));
      });
  };
};

export const sendMail = (workshopId, mail) => {
  return async (dispatch) => {
    console.log(`[async reducer] sendMail(${workshopId}) called`);
    dispatch(setWorkshopSaving(true));
    const token = await getAccessToken();

    //create formdata object to send to endpoint
    const formData = new FormData();
    mail.to.forEach((to) => {
      formData.append(`bcc[]`, to);
    });
    formData.append("subject", mail.subject);
    formData.append("body", mail.body);
    //put attachment file into formdata if there is one
    if (mail.attachment) formData.append("attachment", mail.attachment[0]);

    axios
      .post(`${config.apiEndpoint}/${workshopId}/sendmail`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        dispatch(
          addNotification({
            message: "Email sent",
          })
        );
        dispatch(setWorkshopSaving(false));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while sending",
          error
        );
        dispatch(setWorkshopSaving(false));
      });
  };
};

export const declineOfferedSeat = (workshopId, userMail, sendMail) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] declineOfferedSeat(${workshopId}, ${userMail}, ${sendMail}) called`
    );
    dispatch(setWorkshopSaving(true));
    const token = await getAccessToken();
    axios
      .post(
        `${config.apiEndpoint}/${workshopId}/declineseat?mail=${userMail}&sendMail=${sendMail}`,
        {},
        getBearerHeader(token)
      )
      .then((response) => {
        dispatch(
          addNotification({
            message: `${userMail} is no longer offered a seat`,
          })
        );
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));

        dispatch(setWorkshopSaving(false));
      })
      .catch((error) => {
        errorToNotification(dispatch, "Something went wrong declining", error);
        dispatch(setWorkshopSaving(false));
      });
  };
};

export const sendReminder = (workshopId) => {
  return async (dispatch) => {
    console.log(`[async reducer] sendReminder(${workshopId}) called`);
    dispatch(setWorkshopSaving(true));
    const token = await getAccessToken();
    axios
      .post(
        `${config.apiEndpoint}/${workshopId}/sendreminder`,
        {},
        getBearerHeader(token)
      )
      .then((response) => {
        console.log("response", response);
        dispatch(
          addNotification({
            message: "The reminder has been sent",
          })
        );
        dispatch(setSelectedWorkshop(response.data.data));
        dispatch(setWorkshopSaving(false));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while sending",
          error
        );
        dispatch(setWorkshopSaving(false));
      });
  };
};

export const testAction = () => {
  return async (dispatch) => {
    console.log(`[async reducer] testAction() called`);

    const token = await getAccessToken();
    axios
      .get(`${config.apiEndpoint}/test`, getBearerHeader(token))
      .then((response) => {
        console.log(response);
      })
      .catch((err) => {
        console.error(err.message);
      });
  };
};

//=====================

const getBearerHeader = (token) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  return config;
};

const getAccessToken = async () => {
  if (Providers.globalProvider.state === ProviderState.SignedIn) {
    const token = await Providers.globalProvider.getAccessToken({
      scopes: msalConfig.workshopApiScopes,
    });
    return token;
  }
};

//custom sort function used for sorting workshops
//key should be the attribute to sort on (title, startDate, registrationEndDate, etc...)
//order should be a string "asc" or "desc"
const compareValues = (key, order) => {
  return function innerSort(a, b) {
    //make sure the attribute to sort by exists
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) return 0;
    //get the type of the key
    const varA =
      typeof a[key] === "string"
        ? a[key].toLowerCase()
        : a[key] instanceof Date
        ? Date(a[key])
        : a[key];
    const varB =
      typeof b[key] === "string"
        ? b[key].toLowerCase()
        : b[key] instanceof Date
        ? Date(b[key])
        : b[key];
    let result = 0;
    if (varA > varB) result = 1;
    else if (varA < varB) result = -1;
    return order === "desc" ? result * -1 : result;
  };
};

//display an error in an error notification
//can handle http response data errors
const errorToNotification = (dispatch, message, error) => {
  console.log("error: ", error);
  const displayError =
    error.response === undefined
      ? error.toString()
      : error.response.data.message;
  dispatch(
    addNotification({
      message: message,
      details: displayError,
      type: "error",
    })
  );
};

export default workshopSlice.reducer;
